'use client';

import type React from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import useGetGalleryFilterTags from '@/components/hooks/use-get-gallery-filter-tags';
import useGetImageUrlsByTags from '@/components/hooks/use-get-image-urls-by-tags';
import { GALLERY_MAX_RESULTS, GALLERY_TAG } from '@/lib/consts';

export type GalleryFilter = React.SetStateAction<string>;

interface GalleryImageProviderProperties {
  children: React.ReactNode;
  tags?: string;
  maxResults?: number;
}

interface GalleryImageProviderState {
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  images: string[];
  setFilter: React.Dispatch<GalleryFilter>;
  filter: string;
  filterTags: string[];
  isLoadingFilterTags: boolean;
}

const initialGalleryImageProviderState: GalleryImageProviderState = {
  error: null,
  images: [],
  isFetching: false,
  isLoading: false,
  setFilter: filter => filter,
  filter: '',
  filterTags: [],
  isLoadingFilterTags: false,
};

const GalleryImageContext = createContext(initialGalleryImageProviderState);

export function useGalleryImageProviderContext() {
  return useContext(GalleryImageContext);
}

function GalleryImageProvider({
  children,
  tags = GALLERY_TAG,
  maxResults = GALLERY_MAX_RESULTS,
}: GalleryImageProviderProperties) {
  const [filter, setFilter] = useState(tags);
  const { error, isLoading, isFetching, data } = useGetImageUrlsByTags({
    maxResults,
    tags: filter,
  });
  const { data: filterTags = [], isLoading: isLoadingFilterTags } =
    useGetGalleryFilterTags();

  const context = useMemo(
    () => ({
      error: error?.message ?? null,
      images: data?.map(({ public_id: publicId }) => publicId) ?? [],
      isFetching,
      isLoading,
      setFilter,
      filter,
      filterTags,
      isLoadingFilterTags,
    }),
    [error, isLoading, isFetching, data, filter, filterTags, isLoadingFilterTags],
  );

  return (
    <GalleryImageContext.Provider value={context}>
      {children}
    </GalleryImageContext.Provider>
  );
}

export default GalleryImageProvider;
